import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, RefreshCw, MailCheck, MailX, Mail, AlertCircle } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";

const STATUSES = ["pending", "confirmed", "packed", "shipped", "delivered", "cancelled"];

export default function AdminOrderDetail() {
    const { id } = useParams();
    const { settings } = useSettings();
    const cur = settings?.currency_symbol || "Rs.";
    const [order, setOrder] = useState(null);
    const [tracking, setTracking] = useState({ status: "", tracking_number: "", carrier: "" });
    const [saving, setSaving] = useState(false);
    const [notifs, setNotifs] = useState([]);

    const load = async () => {
        const { data } = await api.get(`/admin/orders/${id}`);
        setOrder(data);
        setTracking({
            status: data.status,
            tracking_number: data.tracking_number || "",
            carrier: data.carrier || "",
        });
    };
    const loadNotifs = async () => {
        try {
            const { data } = await api.get("/admin/notifications", { params: { order_id: id } });
            setNotifs(data || []);
        } catch (_) {}
    };
    useEffect(() => { load(); loadNotifs(); }, [id]);

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/orders/${id}/status`, tracking);
            toast.success("Order updated");
            await load();
            // Notifications fire on backend after status update — refresh log.
            setTimeout(loadNotifs, 600);
        } catch (e) {
            toast.error("Update failed");
        }
        setSaving(false);
    };

    const retry = async (notifId) => {
        try {
            await api.post(`/admin/notifications/${notifId}/retry`);
            toast.success("Re-queued");
            loadNotifs();
        } catch (e) {
            toast.error("Retry failed");
        }
    };

    if (!order) return <div className="text-white/55">Loading…</div>;

    const a = order.shipping_address || {};

    return (
        <div className="space-y-5" data-testid="admin-order-detail">
            <div>
                <Link to="/admin/orders" className="text-xs uppercase tracking-widest text-white/55 hover:text-white inline-flex items-center gap-1">
                    <ChevronLeft size={14} /> All orders
                </Link>
                <h1 className="font-display text-3xl font-black uppercase tracking-tight mt-1">{order.order_number}</h1>
                <div className="text-white/55 text-sm">{new Date(order.created_at).toLocaleString()}</div>
            </div>

            <div className="grid lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 space-y-5">
                    <Card title="Items">
                        <div className="space-y-3">
                            {order.items.map((it, idx) => (
                                <div key={idx} className="flex gap-3 items-center" data-testid={`od-item-${idx}`}>
                                    <div className="w-14 h-14 bg-[#0A0A0B] border border-[#27272A] overflow-hidden flex-shrink-0">
                                        {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-white">{it.name}</div>
                                        {it.variant_label && <div className="text-xs text-white/55 font-display uppercase tracking-widest">{it.variant_label}</div>}
                                        <div className="text-[11px] text-white/45">SKU: {it.sku} · Qty: {it.quantity}</div>
                                    </div>
                                    <div className="font-display font-bold">{formatPrice(it.subtotal || it.price * it.quantity, cur)}</div>
                                </div>
                            ))}
                        </div>
                        <div className="border-t border-[#27272A] mt-4 pt-3 text-sm space-y-1">
                            <div className="flex justify-between"><span className="text-white/65">Subtotal</span><span>{formatPrice(order.subtotal, cur)}</span></div>
                            {order.discount_code && Number(order.discount_amount) > 0 && (
                                <div className="flex justify-between text-[#34C759]" data-testid="od-discount-row">
                                    <span>Discount ({order.discount_code})</span>
                                    <span>− {formatPrice(order.discount_amount, cur)}</span>
                                </div>
                            )}
                            <div className="flex justify-between" data-testid="od-shipping-row">
                                <span className="text-white/65">
                                    Shipping
                                    {order.shipping_type_applied && (
                                        <span className="ml-2 text-[10px] uppercase tracking-widest text-white/45 font-display">
                                            ({order.shipping_type_applied})
                                        </span>
                                    )}
                                </span>
                                <span>{order.shipping === 0 ? "FREE" : formatPrice(order.shipping, cur)}</span>
                            </div>
                            {(order.total_weight_grams || order.chargeable_weight_kg) && (
                                <div className="text-[11px] text-white/45 pt-1" data-testid="od-shipping-meta">
                                    {order.total_weight_grams ? `Total weight: ${order.total_weight_grams} g` : ""}
                                    {order.total_weight_grams && order.chargeable_weight_kg ? " · " : ""}
                                    {order.chargeable_weight_kg ? `Chargeable: ${order.chargeable_weight_kg} kg` : ""}
                                    {order.used_fallback_weight && (
                                        <span className="ml-2 text-[#FF9F0A]">· using fallback weight (some products missing weight)</span>
                                    )}
                                </div>
                            )}
                            <div className="flex justify-between font-display text-lg font-bold pt-2 mt-2 border-t border-[#27272A]"><span>Total</span><span>{formatPrice(order.total, cur)}</span></div>
                        </div>
                    </Card>
                </div>
                <div className="space-y-5">
                    <Card title="Payment">
                        <div className="text-sm space-y-1.5" data-testid="od-payment">
                            <div className="flex justify-between">
                                <span className="text-white/65">Method</span>
                                <span className="font-display uppercase tracking-widest font-bold text-white">
                                    {(order.payment_method || "").toLowerCase() === "razorpay"
                                        ? "Razorpay"
                                        : (order.payment_method || "").toLowerCase() === "cod"
                                          ? "Cash on Delivery"
                                          : (order.payment_method || "—")}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-white/65">Status</span>
                                <span className={`font-display uppercase tracking-widest font-bold ${order.payment_status === "paid" ? "text-[#34C759]" : "text-[#FF9F0A]"}`}>
                                    {order.payment_status || "pending"}
                                </span>
                            </div>
                            {order.razorpay_payment_id && (
                                <div className="flex justify-between gap-2">
                                    <span className="text-white/65">Payment ID</span>
                                    <span className="text-[11px] text-white/85 break-all" data-testid="od-rzp-pid">{order.razorpay_payment_id}</span>
                                </div>
                            )}
                            {Number(order.payment_amount) > 0 && (
                                <div className="flex justify-between">
                                    <span className="text-white/65">Amount paid</span>
                                    <span className="font-medium text-white">{formatPrice(order.payment_amount, cur)}</span>
                                </div>
                            )}
                        </div>
                    </Card>

                    <Card title="Customer">
                        <div className="text-sm space-y-1">
                            <div className="text-white">{a.full_name}</div>
                            <div className="text-white/65">{a.phone}</div>
                            {a.email && <div className="text-white/65">{a.email}</div>}
                        </div>
                        <div className="border-t border-[#27272A] mt-3 pt-3 text-sm text-white/85 leading-relaxed">
                            {a.address_line1}<br />
                            {a.address_line2 && <>{a.address_line2}<br /></>}
                            {a.city}, {a.state} {a.pincode}<br />
                            {a.country}
                        </div>
                        {order.notes && (
                            <div className="border-t border-[#27272A] mt-3 pt-3 text-sm text-white/85">
                                <div className="text-xs uppercase tracking-widest font-display text-white/55 mb-1">Notes</div>
                                {order.notes}
                            </div>
                        )}
                    </Card>

                    <Card title="Status & Tracking">
                        <div>
                            <label className="block text-[11px] uppercase tracking-widest font-display font-bold text-white/55 mb-1">Status</label>
                            <select
                                value={tracking.status}
                                onChange={(e) => setTracking({ ...tracking, status: e.target.value })}
                                className="w-full bg-[#0A0A0B] border border-[#27272A] rounded-sm px-3 py-2 focus:border-[#00BCE5] outline-none"
                                data-testid="od-status"
                            >
                                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[11px] uppercase tracking-widest font-display font-bold text-white/55 mb-1 mt-3">Tracking number</label>
                            <input
                                value={tracking.tracking_number}
                                onChange={(e) => setTracking({ ...tracking, tracking_number: e.target.value })}
                                className="w-full bg-[#0A0A0B] border border-[#27272A] rounded-sm px-3 py-2 focus:border-[#00BCE5] outline-none"
                                data-testid="od-tracking"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] uppercase tracking-widest font-display font-bold text-white/55 mb-1 mt-3">Carrier</label>
                            <input
                                value={tracking.carrier}
                                onChange={(e) => setTracking({ ...tracking, carrier: e.target.value })}
                                placeholder="e.g., Shiprocket / Delhivery"
                                className="w-full bg-[#0A0A0B] border border-[#27272A] rounded-sm px-3 py-2 focus:border-[#00BCE5] outline-none"
                                data-testid="od-carrier"
                            />
                        </div>
                        <button
                            disabled={saving}
                            onClick={save}
                            className="w-full mt-4 bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest py-2.5 rounded-sm hover:bg-[#33C9EA] disabled:opacity-50"
                            data-testid="od-save"
                        >
                            {saving ? "Saving…" : "Update order"}
                        </button>
                    </Card>

                    <Card title="Notifications">
                        {notifs.length === 0 ? (
                            <div className="text-sm text-white/45">No notifications recorded for this order yet.</div>
                        ) : (
                            <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
                                {notifs.map((n) => {
                                    const Icon =
                                        n.status === "sent" ? MailCheck :
                                        n.status === "failed" ? MailX :
                                        n.status === "pending_credentials" ? AlertCircle :
                                        Mail;
                                    const tone =
                                        n.status === "sent" ? "text-[#34C759]" :
                                        n.status === "failed" ? "text-[#FF3B30]" :
                                        n.status === "pending_credentials" ? "text-[#FF9F0A]" :
                                        "text-white/65";
                                    return (
                                        <li key={n.id} className="flex items-start gap-2 border-b border-[#27272A] pb-2" data-testid={`notif-${n.id}`}>
                                            <Icon size={14} className={`${tone} mt-0.5`} />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm text-white truncate">{n.subject || n.event}</div>
                                                <div className="text-[11px] text-white/55 font-display uppercase tracking-widest">
                                                    {n.audience} · {n.event} · <span className={tone}>{n.status.replace("_", " ")}</span>
                                                </div>
                                                <div className="text-[11px] text-white/45 truncate">to {n.to_email || "—"}</div>
                                                {n.error && <div className="text-[11px] text-[#FF6E66] mt-0.5 truncate">{n.error}</div>}
                                            </div>
                                            {(n.status === "failed" || n.status === "pending_credentials") && (
                                                <button
                                                    onClick={() => retry(n.id)}
                                                    className="text-white/65 hover:text-[#00BCE5]"
                                                    title="Retry / re-queue"
                                                    aria-label="Retry notification"
                                                    data-testid={`notif-retry-${n.id}`}
                                                >
                                                    <RefreshCw size={12} />
                                                </button>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        <div className="mt-3 text-[11px] text-white/45 leading-relaxed">
                            Live email send activates when an email provider is configured in <Link to="/admin/settings" className="text-[#00BCE5] hover:underline">Settings → Notifications</Link>.
                            Until then, notifications are queued here with status <span className="text-[#FF9F0A]">pending_credentials</span> and can be retried after configuration.
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
}

function Card({ title, children }) {
    return (
        <section className="bg-[#18181B] border border-[#27272A] rounded-sm">
            <div className="px-4 py-3 border-b border-[#27272A] font-display uppercase font-black tracking-tight">{title}</div>
            <div className="p-4 space-y-2">{children}</div>
        </section>
    );
}
